import { motion } from "framer-motion";
import { Rocket, Award, Pill } from "lucide-react";
import { ChapterShell } from "../ChapterShell";
import { FadeUp } from "../Reveal";
import { HEADSHOT_FALLBACK } from "../../data/content";
import { imgFallback } from "../../utils";

export const ChapterFive = () => (
  <ChapterShell
    n="05"
    id="chapter-5"
    era="Urology Division + Critical Care division as additional responsibility"
    title="Scaling Critical Care"
    quote="From building a business to scaling a business."
    dark
  >
    <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <FadeUp>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#00E5FF]">
            Critical Care division + Urology division
          </p>
          <p className="mt-3 font-display text-7xl font-black tracking-tight text-white md:text-8xl">
            ₹114<span className="text-[#00E5FF]"> Cr</span>
          </p>
          <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white/50">
            ₹104 Cr in Critical Care + ₹10 Cr in Urology
          </p>
          <p className="mt-3 max-w-lg text-sm leading-relaxed text-white/60 md:text-base">
            Entrusted with the Critical Care Division of Micro Labs — driving priority brands through high-impact
            messaging, collateral and launch excellence.
          </p>
          <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#00E5FF]/30 bg-[#00E5FF]/10 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.16em] text-[#00E5FF]">
            Additional responsibility — scaling Critical Care along with the Urology division
          </p>
        </FadeUp>

        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="glass-dark rounded-3xl p-7"
            data-testid="micropenam-card"
          >
            <Award size={22} className="text-[#00E5FF]" />
            <p className="mt-4 font-display text-4xl font-black text-white">
              Micropenam <span className="text-[#00E5FF]">₹15.4 Cr</span>
            </p>
            <p className="mt-3 text-xs leading-relaxed text-white/60">
              First Critical Care brand of Micro Labs to cross ₹15 Cr annual sales (FY 21–22).
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="glass-dark animate-glow relative overflow-hidden rounded-3xl p-7"
            data-testid="zambact-card"
          >
            <div className="pointer-events-none absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-transparent via-[#00E5FF] to-transparent" />
            <Rocket size={22} className="text-[#00BFA5]" />
            <p className="mt-4 font-display text-4xl font-black text-white">
              Zambact-A <span className="text-[#00BFA5]">₹4.4 Cr</span>
            </p>
            <p className="mt-3 text-xs leading-relaxed text-white/60">
              Ceftazidime + Avibactam Inj. — a ₹4.4 Cr brand in the very first year of launch.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="glass-dark rounded-3xl p-7 sm:col-span-2"
            data-testid="avoprost-group-card"
          >
            <Pill size={22} className="text-[#00E5FF]" />
            <p className="mt-4 font-display text-4xl font-black text-white">
              Avoprost Group <span className="text-[#00E5FF]">₹5.5 Cr</span>
            </p>
            <p className="mt-3 text-xs leading-relaxed text-white/60">
              A ₹5.5 Cr brand group — Silodosin and Silodosin + Dutasteride.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="lg:col-span-5">
        <FadeUp delay={0.15} className="relative mx-auto max-w-xs">
          <div className="absolute -inset-6 rounded-[2rem] bg-[#00E5FF]/15 blur-2xl" />
          <div className="glass-dark relative overflow-hidden rounded-[1.8rem] p-2">
            <img
              src="/assets/avatar-dashboard.png"
              onError={imgFallback(HEADSHOT_FALLBACK)}
              alt="Studying the critical care dashboard"
              data-testid="chapter5-avatar"
              className="w-full rounded-[1.4rem] object-cover"
            />
          </div>
          <p className="mt-5 text-center text-[11px] font-bold uppercase tracking-[0.25em] text-white/40">
            Watching the numbers move
          </p>
        </FadeUp>
      </div>
    </div>
  </ChapterShell>
);
